import { useRef, useEffect, useCallback, MutableRefObject } from 'react';
import { BrightcovePlayerRef } from '../AVideo.types';

export interface UseVideoPlayStateOptions {
  isPlaying?: boolean;
  onAutoplayBlocked?: () => void;
}

export interface UseVideoPlayStateResult {
  playerRef: MutableRefObject<BrightcovePlayerRef | null>;
  pendingIsPlayingRef: MutableRefObject<boolean | undefined>;
  /**
   * Call inside your player's onSuccess/onReady handler.
   * Sets playerRef.current and applies any isPlaying intent that arrived before the player was ready.
   */
  applyPendingPlayState: (player: BrightcovePlayerRef) => void;
}

/** HTMLMediaElement.readyState value at which playback can begin without stalling. */
const READY_STATE_CAN_PLAY = 3; // HAVE_FUTURE_DATA

const getReadyState = (player: BrightcovePlayerRef): number =>
  typeof player.readyState === 'function' ? player.readyState() : 0;

/** Readiness events any of which signals the player has buffered enough to start. */
const READINESS_EVENTS = ['canplay', 'loadeddata', 'playing'];

/**
 * Requests playback from a managed player, tolerant of the player not being ready yet.
 *
 * On Windows the player is frequently asked to play before it has buffered any
 * data (readyState=0). In that state play() either rejects or silently no-ops and
 * the video never starts, which is why autoplay works on some platforms (e.g. macOS)
 * but not on Windows. This affects Chromium browsers such as Chrome and Edge.
 *
 * Strategy:
 *  - Attempt play() right away. When the player is ready this starts playback; on
 *    platforms where a not-ready play() rejects, the rejection is handled below.
 *  - On rejection we report autoplay blocked (so the UI can fall back, e.g. show a
 *    poster/controls) and, when possible, also arm readiness listeners
 *    (canplay/loadeddata/playing) that retry play() once. This covers the
 *    "play() silently did nothing" case too, because the readiness event fires
 *    regardless of whether the first promise rejected. Listeners are always removed
 *    once one fires so a later buffering cycle can never replay.
 */
function playWithRetry(player: BrightcovePlayerRef, onBlocked?: () => void): void {
  const canListen = typeof player.on === 'function' && typeof player.off === 'function';

  // Guards against retrying more than once if several readiness events fire in
  // quick succession before all listeners are detached.
  let hasRetried = false;
  const retry = (): void => {
    if (hasRetried) return;
    hasRetried = true;
    READINESS_EVENTS.forEach(event => player.off!(event, retry));
    player.play?.()?.catch(() => {});
  };

  player.play?.()?.catch(() => {
    onBlocked?.();
    // Arm a one-shot readiness retry so a video that was merely not-ready still
    // starts once it buffers, without waiting on another isPlaying transition.
    if (canListen && getReadyState(player) < READY_STATE_CAN_PLAY) {
      READINESS_EVENTS.forEach(event => player.on!(event, retry));
    }
  });
}

/**
 * Shared imperative play/pause hook for managed video players (Brightcove and future players).
 * Holds the pending-play ref pattern so player sub-components do not re-implement it.
 *
 * Uses a stable ref for onAutoplayBlocked to avoid stale closures in effects.
 *
 * Race-condition safety: applyPendingPlayState sets playerRef.current before
 * reading pendingIsPlayingRef, so the isPlaying effect and this callback never
 * disagree about whether the player is ready. Under React 18 concurrent mode
 * the effect may re-run, but because both paths converge on playerRef.current
 * the worst case is a redundant play()/pause() call, never a missed one.
 */
export function useVideoPlayState({ isPlaying, onAutoplayBlocked }: UseVideoPlayStateOptions): UseVideoPlayStateResult {
  const playerRef = useRef<BrightcovePlayerRef | null>(null);
  const pendingIsPlayingRef = useRef<boolean | undefined>(undefined);
  const onAutoplayBlockedRef = useRef(onAutoplayBlocked);

  useEffect(() => {
    onAutoplayBlockedRef.current = onAutoplayBlocked;
  }, [onAutoplayBlocked]);

  useEffect(() => {
    if (isPlaying === undefined) return;

    if (playerRef.current) {
      if (isPlaying) {
        playWithRetry(playerRef.current, () => onAutoplayBlockedRef.current?.());
      } else {
        playerRef.current.pause?.();
      }
    } else {
      pendingIsPlayingRef.current = isPlaying;
    }
  }, [isPlaying]);

  const applyPendingPlayState = useCallback((player: BrightcovePlayerRef) => {
    playerRef.current = player;
    const pending = pendingIsPlayingRef.current;
    pendingIsPlayingRef.current = undefined;

    if (pending === true) {
      playWithRetry(player, () => onAutoplayBlockedRef.current?.());
    } else if (pending === false) {
      player.pause?.();
    }
  }, []);

  return { playerRef, pendingIsPlayingRef, applyPendingPlayState };
}
